import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import { EDUCATION_DEFAULTS } from '../../../data/educationDefaults';
import './EducationLearningApproachForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function EducationLearningApproachForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const defaults = EDUCATION_DEFAULTS.learning_approach;

  const [formData, setFormData] = useState({
    title: defaults.title || '',
    subtitle: defaults.subtitle || '',
    description: defaults.description || '',
    pedagogicalModel: defaults.pedagogicalModel ? { ...defaults.pedagogicalModel } : {},
    signatureStrategies: defaults.signatureStrategies ? [...defaults.signatureStrategies] : [],
    integration: defaults.integration || ''
  });

  const [originalData, setOriginalData] = useState({ ...formData });
  const [pendingFields, setPendingFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [activeTab, setActiveTab] = useState('overview-header');
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [editingPhaseIndex, setEditingPhaseIndex] = useState(null);
  const [phaseModalData, setPhaseModalData] = useState(null);
  const [editingStrategyIndex, setEditingStrategyIndex] = useState(null);
  const [strategyModalData, setStrategyModalData] = useState(null);
  const [mediaMap, setMediaMap] = useState({});

  useEffect(() => {
    loadMediaMap();
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/education/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) return {};
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('Media fetch error:', err.message);
    }
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let result = null;
      
      // Try authenticated endpoint first
      console.log('[EducationLearningApproachForm] Trying authenticated endpoint...');
      const adminResponse = await fetch(
        `${apiBase}/education/cms/sections`,
        { credentials: 'include', headers: { 'Content-Type': 'application/json' } }
      );
      
      if (adminResponse.ok) {
        result = await adminResponse.json();
        console.log('[EducationLearningApproachForm] ✓ Got data from admin endpoint');
      } else if (adminResponse.status === 401) {
        console.log('[EducationLearningApproachForm] Admin endpoint unauthorized, trying public endpoint...');
        // Fallback to public endpoint
        const publicResponse = await fetch(
          `${apiBase}/education/cms/sections?published=true`,
          { credentials: 'include', headers: { 'Content-Type': 'application/json' } }
        );
        
        if (publicResponse.ok) {
          result = await publicResponse.json();
          console.log('[EducationLearningApproachForm] ✓ Got data from public endpoint');
        } else {
          throw new Error(`Public endpoint failed with status ${publicResponse.status}`);
        }
      } else {
        throw new Error(`Admin endpoint failed with status ${adminResponse.status}`);
      }
      
      if (result.ok && Array.isArray(result.data)) {
        console.log('[EducationLearningApproachForm] Received sections:', result.data.map(s => s.section_type));
        // Try with -shell suffix first, then without
        let section = result.data.find(s => s.section_type === 'education-learning-approach-shell');
        if (!section) {
          section = result.data.find(s => s.section_type === 'education-learning-approach');
        }
        if (section) {
          setSectionId(section.id);
          console.log('✓ Loaded learning approach section ID:', section.id);
          
          // Check for pending changes
          let pendingFieldsMap = {};
          try {
            const pendingResponse = await fetch(
              `${apiBase}/cms/section/${section.id}/with-pending`,
              { credentials: 'include', headers: { 'Content-Type': 'application/json' } }
            );
            
            if (pendingResponse.ok) {
              const pendingResult = await pendingResponse.json();
              if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
                const { pending_changes } = pendingResult.data;
                if (Array.isArray(pending_changes)) {
                  pending_changes.forEach(change => {
                    if (change.status === 'pending' && change.locale === 'en') {
                      // Check for strategy pending
                      if (change.field_id && change.field_id.includes('signature_strategies.')) {
                        const match = change.field_id.match(/signature_strategies\.(\d+)\./);
                        if (match) {
                          const strategyIdx = parseInt(match[1]);
                          pendingFieldsMap[`strategy_${strategyIdx}`] = true;
                        }
                      }
                      // Check for phase pending
                      if (change.field_id && change.field_id.includes('pedagogical_model.phases.')) {
                        const match = change.field_id.match(/pedagogical_model\.phases\.(\d+)\./);
                        if (match) {
                          const phaseIdx = parseInt(match[1]);
                          pendingFieldsMap[`phase_${phaseIdx}`] = true;
                        }
                      }
                    }
                  });
                }
              }
            }
          } catch (err) {
            console.warn('[EducationLearningApproachForm] Pending changes check failed:', err.message);
          }
          
          setPendingFields(pendingFieldsMap);
          
          const content = section.content_published || section.content || {};
          const en = content.en || {};
          
          const normalizedData = {
            title: en.title || defaults.title || '',
            subtitle: en.subtitle || defaults.subtitle || '',
            description: en.description || defaults.description || '',
            pedagogicalModel: en.pedagogical_model || en.pedagogicalModel || defaults.pedagogicalModel || {},
            signatureStrategies: en.signature_strategies || en.signatureStrategies || defaults.signatureStrategies || [],
            integration: en.integration || defaults.integration || ''
          };
          
          setFormData(normalizedData);
          setOriginalData({ ...normalizedData });
        } else {
          console.error('✗ Learning approach section not found in API response');
          console.error('Available section types:', result.data.map(s => s.section_type));
          toast.error('Section not found. Please check with administrator.');
        }
      } else {
        console.error('✗ Invalid API response:', result);
        toast.error('API error loading section');
      }
    } catch (err) {
      console.error('Error loading section:', err.message);
      toast.error('Failed to load section');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, type, index) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    const uploadKey = `${type}_${index}`;
    setImageUploading(prev => ({ ...prev, [uploadKey]: true }));
    setImageProgress(prev => ({ ...prev, [uploadKey]: 0 }));

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setImageProgress(prev => ({ ...prev, [uploadKey]: percent }));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              if (mediaId) {
                if (type === 'strategy') {
                  setStrategyModalData(prev => ({...prev, image: mediaId}));
                }
                toast.success('✓ Image uploaded');
              }
            }
          } catch (parseErr) {
            toast.error('Invalid server response');
          }
        }
        setImageUploading(prev => ({ ...prev, [uploadKey]: false }));
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload failed');
        setImageUploading(prev => ({ ...prev, [uploadKey]: false }));
      });

      xhr.open('POST', `${apiBase}/education/cms/media/upload`);
      xhr.withCredentials = true;
      xhr.send(formDataToUpload);
    } catch (err) {
      toast.error('Upload error');
      setImageUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const saveOverview = async () => {
    setSubmitting(prev => ({ ...prev, 'overview-header': true }));
    try {
      const payload = {
        content_published: JSON.stringify({
          en: {
            title: formData.title,
            subtitle: formData.subtitle,
            description: formData.description,
            pedagogical_model: formData.pedagogicalModel,
            signature_strategies: formData.signatureStrategies,
            integration: formData.integration
          }
        })
      };

      const response = await fetch(
        `${apiBase}/education/cms/section/${sectionId}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) throw new Error('Save failed');

      const result = await response.json();
      if (result.success || result.ok) {
        toast.success('Overview saved');
      } else {
        throw new Error('Save failed');
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save overview');
    } finally {
      setSubmitting(prev => ({ ...prev, 'overview-header': false }));
    }
  };

  const editPhase = (index) => {
    setEditingPhaseIndex(index);
    setPhaseModalData({...formData.pedagogicalModel.phases[index]});
  };

  const savePhase = async () => {
    if (!phaseModalData || !phaseModalData.phase || !phaseModalData.details) {
      toast.warning('Please fill in all fields');
      return;
    }

    if (!sectionId) {
      toast.error('Section ID not available');
      return;
    }

    const phaseKey = `phase_${editingPhaseIndex}`;
    setSubmitting(prev => ({ ...prev, [phaseKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `pedagogical_model.phases.${editingPhaseIndex}.update`,
            field_name: `Update Phase ${editingPhaseIndex + 1}: ${phaseModalData.phase}`,
            old_value: originalData.pedagogicalModel.phases[editingPhaseIndex] || {},
            new_value: phaseModalData,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Phase ${editingPhaseIndex + 1} submitted for approval`);
        
        setPendingFields(prev => ({
          ...prev,
          [`phase_${editingPhaseIndex}`]: true
        }));

        const updated = { ...formData };
        updated.pedagogicalModel.phases[editingPhaseIndex] = phaseModalData;
        setFormData(updated);
        setOriginalData(prev => ({
          ...prev,
          pedagogicalModel: {
            ...prev.pedagogicalModel,
            phases: prev.pedagogicalModel.phases.map((p, i) => i === editingPhaseIndex ? phaseModalData : p)
          }
        }));

        setEditingPhaseIndex(null);
        setPhaseModalData(null);
      } else {
        if (response.status === 409) {
          toast.error(`Phase ${editingPhaseIndex + 1} already has a pending approval`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Failed to save phase');
    } finally {
      setSubmitting(prev => ({ ...prev, [phaseKey]: false }));
    }
  };

  const editStrategy = (index) => {
    setEditingStrategyIndex(index);
    setStrategyModalData({...formData.signatureStrategies[index]});
  };

  const saveStrategy = async () => {
    if (!strategyModalData || !strategyModalData.title || !strategyModalData.description) {
      toast.warning('Please fill in all fields');
      return;
    }

    if (!sectionId) {
      toast.error('Section ID not available');
      return;
    }

    const strategyKey = `strategy_${editingStrategyIndex}`;
    setSubmitting(prev => ({ ...prev, [strategyKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `signature_strategies.${editingStrategyIndex}.update`,
            field_name: `Update Strategy ${editingStrategyIndex + 1}: ${strategyModalData.title}`,
            old_value: originalData.signatureStrategies[editingStrategyIndex] || {},
            new_value: strategyModalData,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Strategy ${editingStrategyIndex + 1} submitted for approval`);
        
        setPendingFields(prev => ({
          ...prev,
          [`strategy_${editingStrategyIndex}`]: true
        }));

        const updated = { ...formData };
        updated.signatureStrategies[editingStrategyIndex] = strategyModalData;
        setFormData(updated);
        setOriginalData(prev => ({
          ...prev,
          signatureStrategies: prev.signatureStrategies.map((s, i) => i === editingStrategyIndex ? strategyModalData : s)
        }));

        setEditingStrategyIndex(null);
        setStrategyModalData(null);
      } else {
        if (response.status === 409) {
          toast.error(`Strategy ${editingStrategyIndex + 1} already has a pending approval`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Failed to save strategy');
    } finally {
      setSubmitting(prev => ({ ...prev, [strategyKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="education-learning-approach-form-container">
        <div className="hero-form-wrapper">
          <div className="loading">
            <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="education-learning-approach-form-container">
      <ToastContainer />

      <div className="hero-form-wrapper">
        <div className="tabs-wrapper">
          <div className="tabs-nav">
            <button
              className={`tab-button ${activeTab === 'overview-header' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview-header')}
            >
              Overview
            </button>
            <button
              className={`tab-button ${activeTab === 'model' ? 'active' : ''}`}
              onClick={() => setActiveTab('model')}
            >
              5E Model
            </button>
            <button
              className={`tab-button ${activeTab === 'strategies' ? 'active' : ''}`}
              onClick={() => setActiveTab('strategies')}
            >
              Learning Strategies
            </button>
          </div>
        </div>

        {activeTab === 'overview-header' && (
          <div className="form-section">
            <h3 className="form-section-title">Section Overview</h3>
            <div className="form-field-card">
              <label>Title</label>
              <input type="text" className="text-input" value={formData.title} onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))} maxLength="100" />
            </div>
            <div className="form-field-card">
              <label>Subtitle</label>
              <input type="text" className="text-input" value={formData.subtitle} onChange={(e) => setFormData(prev => ({...prev, subtitle: e.target.value}))} maxLength="200" />
            </div>
            <div className="form-field-card">
              <label>Description</label>
              <textarea className="textarea-input" value={formData.description} onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))} rows={3} maxLength="500" />
            </div>
            <button className="submit-btn" onClick={saveOverview} disabled={submitting['overview-header']}>
              {submitting['overview-header'] ? 'Saving...' : 'Save Overview'}
            </button>
          </div>
        )}

        {activeTab === 'model' && (
          <div className="form-section">
            <h3 className="form-section-title">5E Instructional Model</h3>
            
            <div style={{marginBottom: '24px'}}>
              <h4 style={{fontSize: '16px', fontWeight: 700, color: '#1a202c', marginBottom: '16px'}}>Phases</h4>
              {Array.isArray(formData.pedagogicalModel?.phases) && formData.pedagogicalModel.phases.map((phase, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => editPhase(idx)}
                  className="level-card"
                  style={{marginBottom: '16px'}}
                >
                  <div className="level-card-content">
                    <div className="level-card-header">
                      <h4 className="level-card-title">{phase.phase}</h4>
                      {pendingFields[`phase_${idx}`] && (
                        <span className="level-card-badge">⏳</span>
                      )}
                    </div>
                    <p className="level-card-subtitle">{phase.description}</p>
                    <p className="level-card-desc">{phase.details}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'strategies' && (
          <div className="form-section">
            <h3 className="form-section-title">Signature Learning Strategies</h3>
            
            <div style={{marginBottom: '32px'}}>
              <div className="level-cards-grid">
                {Array.isArray(formData.signatureStrategies) && formData.signatureStrategies.map((strategy, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="level-card"
                    onClick={() => editStrategy(idx)}
                  >
                    <div className="level-card-image">
                      {strategy.image && mediaMap[strategy.image] && (
                        <img src={mediaMap[strategy.image]} alt={strategy.title} />
                      )}
                      {(!strategy.image || !mediaMap[strategy.image]) && (
                        <div className="level-card-image-placeholder">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                            <polyline points="21 15 16 10 5 21"></polyline>
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="level-card-content">
                      <div className="level-card-header">
                        <h4 className="level-card-title">{strategy.title}</h4>
                        {pendingFields[`strategy_${idx}`] && (
                          <span className="level-card-badge">⏳</span>
                        )}
                      </div>
                      <p className="level-card-desc">{strategy.description.substring(0, 60)}...</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section-title">Integration Across Subjects</h3>
              <div className="form-field-card">
                <label>Integration Description</label>
                <textarea
                  className="textarea-input"
                  value={formData.integration}
                  onChange={(e) => setFormData(prev => ({...prev, integration: e.target.value}))}
                  rows={3}
                  maxLength="500"
                />
              </div>
              <button className="submit-btn" onClick={saveOverview} disabled={submitting['overview-header']} style={{marginTop: '16px'}}>
                {submitting['overview-header'] ? 'Saving...' : 'Save Integration'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* PHASE EDIT MODAL */}
      {editingPhaseIndex !== null && phaseModalData && (
        <div className="modal-overlay" onClick={() => {
          setEditingPhaseIndex(null);
          setPhaseModalData(null);
        }}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Phase: {phaseModalData.phase}</h3>
              <button className="modal-close" onClick={() => {
                setEditingPhaseIndex(null);
                setPhaseModalData(null);
              }}>×</button>
            </div>

            <div className="modal-body">
              <div className="modal-field">
                <label>Details *</label>
                <textarea
                  className="modal-textarea"
                  value={phaseModalData.details}
                  onChange={(e) => setPhaseModalData(prev => ({...prev, details: e.target.value}))}
                  rows={4}
                  maxLength="500"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => {
                setEditingPhaseIndex(null);
                setPhaseModalData(null);
              }}>Cancel</button>
              <button
                className="btn-submit"
                onClick={savePhase}
                disabled={submitting[`phase_${editingPhaseIndex}`] || pendingFields[`phase_${editingPhaseIndex}`]}
              >
                {submitting[`phase_${editingPhaseIndex}`] ? 'Saving...' : pendingFields[`phase_${editingPhaseIndex}`] ? 'Pending Approval' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STRATEGY EDIT MODAL */}
      {editingStrategyIndex !== null && strategyModalData && (
        <div className="modal-overlay" onClick={() => {
          setEditingStrategyIndex(null);
          setStrategyModalData(null);
        }}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Strategy: {strategyModalData.title}</h3>
              <button className="modal-close" onClick={() => {
                setEditingStrategyIndex(null);
                setStrategyModalData(null);
              }}>×</button>
            </div>

            <div className="modal-body">
              <div className="modal-field">
                <label>Strategy Image</label>
                {strategyModalData.image && mediaMap[strategyModalData.image] && (
                  <div className="modal-image-preview">
                    <img src={mediaMap[strategyModalData.image]} alt="Strategy preview" />
                  </div>
                )}
                <div className="image-upload-area">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => handleImageUpload(e, 'strategy', editingStrategyIndex)}
                    disabled={imageUploading[`strategy_${editingStrategyIndex}`]}
                    className="image-input"
                    id="strategy-modal-image-input"
                  />
                  <label htmlFor="strategy-modal-image-input" className={`upload-label ${imageUploading[`strategy_${editingStrategyIndex}`] ? 'uploading' : ''}`}>
                    {imageUploading[`strategy_${editingStrategyIndex}`] ? `Uploading ${imageProgress[`strategy_${editingStrategyIndex}`]}%...` : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline', marginRight: '4px', verticalAlign: 'middle'}}>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="17 8 12 3 7 8"></polyline>
                          <line x1="12" y1="3" x2="12" y2="15"></line>
                        </svg>
                        Upload Image
                      </>
                    )}
                  </label>
                </div>
                <span className="hint">Max 5MB • JPG, PNG, WebP</span>
              </div>

              <div className="modal-field">
                <label>Title *</label>
                <input
                  type="text"
                  className="modal-input"
                  value={strategyModalData.title}
                  onChange={(e) => setStrategyModalData(prev => ({...prev, title: e.target.value}))}
                  maxLength="100"
                />
              </div>

              <div className="modal-field">
                <label>Description *</label>
                <textarea
                  className="modal-textarea"
                  value={strategyModalData.description}
                  onChange={(e) => setStrategyModalData(prev => ({...prev, description: e.target.value}))}
                  rows={4}
                  maxLength="500"
                />
              </div>

              <div className="modal-field">
                <label>Outcomes</label>
                {strategyModalData.outcomes?.map((outcome, idx) => (
                  <input
                    key={idx}
                    type="text"
                    className="modal-input"
                    value={outcome}
                    onChange={(e) => {
                      const updated = [...strategyModalData.outcomes];
                      updated[idx] = e.target.value;
                      setStrategyModalData(prev => ({...prev, outcomes: updated}));
                    }}
                    placeholder="Outcome..."
                    style={{marginBottom: '8px'}}
                  />
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => {
                setEditingStrategyIndex(null);
                setStrategyModalData(null);
              }}>Cancel</button>
              <button
                className="btn-submit"
                onClick={saveStrategy}
                disabled={submitting[`strategy_${editingStrategyIndex}`] || pendingFields[`strategy_${editingStrategyIndex}`]}
              >
                {submitting[`strategy_${editingStrategyIndex}`] ? 'Saving...' : pendingFields[`strategy_${editingStrategyIndex}`] ? 'Pending Approval' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
